import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { PageHero, Reveal, SectionTitle, Icon, Btn, Kicker } from '../components/common';
import { img, programs, membership } from '../mock/mock';
import { Check, Calendar, MapPin } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const filters = [
  { key: 'all', label: 'All' },
  { key: 'education', label: 'Education' },
  { key: 'events', label: 'Events' },
];

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return ''; }
};

const Programs = () => {
  const { toast } = useToast();
  const [filter, setFilter] = useState('all');
  const [events, setEvents] = useState([]);
  const list = filter === 'all' ? programs : programs.filter((p) => p.cat === filter);

  useEffect(() => {
    axios.get(`${API}/events`)
      .then((res) => setEvents(res.data || []))
      .catch(() => setEvents([]));
  }, []);

  return (
    <div>
      <PageHero image={img.studentFlowers} kicker="Programs & Experiences" title="Where wonder becomes a way of life" sub="From school field trips to garden concerts, discover the experiences awaiting every visitor." />

      {/* Programs */}
      <section id="education" className="py-24 bg-cream scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><SectionTitle center kicker="What Awaits" title="Programs for every age & season" /></Reveal>
          <div className="flex justify-center gap-3 mt-8">
            {filters.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)} className={`px-6 py-2.5 rounded-full text-sm font-600 transition-all ${filter === f.key ? 'bg-forest text-cream' : 'bg-white text-forest border border-sand hover:border-forest'}`}>{f.label}</button>
            ))}
          </div>
          <div id="events" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12 scroll-mt-24">
            {list.map((p, i) => (
              <Reveal key={p.title} delay={(i % 3) * 100}>
                <div className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:-translate-y-1.5 transition-transform duration-300 h-full">
                  <div className="relative h-52 overflow-hidden">
                    <img src={img[p.img]} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-cream/95 text-forest text-xs font-600 capitalize">{p.cat}</span>
                    <div className="absolute bottom-4 left-4 w-11 h-11 rounded-xl bg-forest/90 flex items-center justify-center">
                      <Icon name={p.icon} className="w-5 h-5 text-gold-light" />
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-xl text-forest">{p.title}</h3>
                    <p className="text-ink/65 text-sm mt-2 leading-relaxed">{p.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      {events.length > 0 && (
        <section id="upcoming" className="py-24 bg-sand scroll-mt-24">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <Reveal><SectionTitle center kicker="Save the Date" title="Upcoming events" sub="Previews, festivals, workshops and celebrations as the garden comes to life." /></Reveal>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
              {events.map((ev, i) => (
                <Reveal key={ev.id} delay={(i % 3) * 100}>
                  <div className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:-translate-y-1.5 transition-transform duration-300 h-full flex flex-col">
                    <div className="relative h-44 overflow-hidden">
                      <img src={img[ev.image_key] || img.meadow} alt={ev.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                      <span className="absolute top-4 left-4 px-3 py-1 rounded-full bg-forest/90 text-cream text-xs font-600 flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{fmtDate(ev.date)}</span>
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <span className="text-gold text-xs font-600 tracking-wide uppercase capitalize">{ev.category}</span>
                      <h3 className="font-display text-xl text-forest mt-1">{ev.title}</h3>
                      <p className="text-ink/65 text-sm mt-2 leading-relaxed flex-1">{ev.description}</p>
                      <div className="flex items-center gap-2 text-ink/50 text-xs mt-4 pt-4 border-t border-sand"><MapPin className="w-3.5 h-3.5" />{ev.location}</div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Membership */}
      <section id="membership" className="py-24 bg-forest text-cream scroll-mt-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <Reveal><SectionTitle center light kicker="Membership" title="Belong to the garden" sub="Members enjoy year-round access, exclusive previews and the joy of watching the garden grow." /></Reveal>
          <div className="grid md:grid-cols-3 gap-6 mt-14 items-stretch">
            {membership.map((m, i) => (
              <Reveal key={m.name} delay={i * 120}>
                <div className={`h-full rounded-2xl p-8 border-2 flex flex-col ${m.featured ? 'border-gold bg-cream text-ink scale-[1.03]' : 'border-forest-light/30 bg-forest-dark/50'}`}>
                  {m.featured && <span className="self-start px-3 py-1 rounded-full bg-gold text-white text-xs font-600 mb-4">Best Value</span>}
                  <h3 className={`font-display text-2xl ${m.featured ? 'text-forest' : 'text-white'}`}>{m.name}</h3>
                  <div className={`font-display text-3xl mt-2 ${m.featured ? 'text-forest' : 'text-gold-light'}`}>{m.price}</div>
                  <ul className="mt-6 space-y-3 flex-1">
                    {m.perks.map((p) => (
                      <li key={p} className={`flex gap-3 text-sm ${m.featured ? 'text-ink/70' : 'text-cream/80'}`}>
                        <Check className="w-4 h-4 shrink-0 mt-0.5 text-gold" />{p}
                      </li>
                    ))}
                  </ul>
                  <button onClick={() => toast({ title: `${m.name} membership`, description: 'Visual demo — membership checkout can be connected later.' })} className={`mt-8 py-3.5 rounded-full font-600 text-sm transition-colors ${m.featured ? 'bg-gold text-white hover:bg-gold-light' : 'border-2 border-cream/50 text-cream hover:bg-cream hover:text-forest'}`}>Become a Member</button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-sand">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <Reveal>
            <Kicker>Plan Ahead</Kicker>
            <h2 className="font-display text-3xl md:text-4xl text-forest mt-4">Reserve your place among the first to visit</h2>
            <p className="mt-4 text-ink/65">Join our list and be first to know when previews and programs open.</p>
            <div className="mt-8 flex justify-center"><Btn to="/contact">Get in touch</Btn></div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Programs;
